/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useRef } from "react";

import { useAccessibilityFocusSync } from "../../hooks/useAccessibilityFocusSync";
import type { ActionButtonProps } from "./ActionButtonProps";

import "./ActionButton.scss";

const ActionButton: FunctionComponent<ActionButtonProps> = (
  props: ActionButtonProps
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "ActionButton";
  const {
    label,
    onClick,
    isFocused,
    isAccessibilityFocused,
    isDisabled,
    className
  } = props;

  // VoiceView: real DOM focus follows the D-pad focus.
  const buttonRef = useRef<HTMLButtonElement>(null);
  useAccessibilityFocusSync(buttonRef, isAccessibilityFocused === true);

  const classNames: string =
    "action-button" +
    (isFocused === true ? " action-button--focused" : "") +
    (isDisabled === true ? " action-button--disabled" : "") +
    (className !== undefined ? " " + className : "");

  return (
    <button
      ref={buttonRef}
      className={classNames}
      type="button"
      tabIndex={0}
      onClick={onClick}
      disabled={isDisabled === true}
    >
      {label}
    </button>
  );
};

export default ActionButton;
